import React, { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, Search, Trash2,
  ChevronUp, ChevronDown, CheckCircle2, XCircle, Star, Loader2,
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../../styles/AdminGlobal.css';
import '../../styles/AdminShared.css';
import './Admin.css';
import '../../components/admin/MobileCardList.css';

interface Review {
  id: number;
  customer_name: string;
  product_name: string;
  rating: number;
  comment: string;
  is_approved: boolean;
  created_at: string;
}

const ReviewManagement: React.FC = () => {
  const isMobile = useIsMobile();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortDir, setSortDir] = useState<'asc'|'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });


  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getReviews();
      const data = response.data || response;
      const reviewsList = Array.isArray(data) ? data : (data.results || data.data || []);
      setReviews(reviewsList);
    } catch (error) {
      console.error('Failed to load reviews:', error);
      setToast({ show: true, message: 'Failed to load reviews', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (review: Review) => {
    try {
      await adminAPI.approveReview(review.id);
      setToast({ show: true, message: 'Review approved successfully', type: 'success' });
      loadReviews();
    } catch (error) {
      console.error('Approve error:', error);
      setToast({ show: true, message: 'Failed to approve review', type: 'error' });
    }
  };

  const handleReject = async (review: Review) => {
    try {
      // Assuming there's a reject endpoint similar to approve
      await adminAPI.rejectReview(review.id);
      setToast({ show: true, message: 'Review rejected', type: 'success' });
      loadReviews();
    } catch (error) {
      console.error('Reject error:', error);
      setToast({ show: true, message: 'Failed to reject review', type: 'error' });
    }
  };

  const handleDeleteClick = (review: Review) => {
    setSelectedReview(review);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedReview) return;
    
    try {
      await adminAPI.deleteReview(selectedReview.id);
      setToast({ show: true, message: 'Review deleted successfully', type: 'success' });
      loadReviews();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete review', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedReview(null);
    }
  };

  const handleSort = (f: string) => {
    if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(f); setSortDir('asc'); }
    setCurrentPage(1);
  };
  const SA = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} className="adm-sort-arrow" /> : <ChevronDown size={10} className="adm-sort-arrow" />;
  };
  const Stars = ({ n }: { n: number }) => <span className="adm-stars">{'★'.repeat(n)}{'☆'.repeat(5 - n)}</span>;

  const reviewsArray = Array.isArray(reviews) ? reviews : [];
  const filtered = useMemo(() => {
    let list = reviewsArray;
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(r =>
        r.customer_name?.toLowerCase().includes(q) ||
        r.product_name?.toLowerCase().includes(q) ||
        r.comment?.toLowerCase().includes(q)
      );
    }
    if (statusFilter === 'approved') list = list.filter(r => r.is_approved);
    else if (statusFilter === 'pending') list = list.filter(r => !r.is_approved);
    list = [...list].sort((a: any, b: any) => {
      const av = a[sortField] ?? ''; const bv = b[sortField] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [reviewsArray, searchTerm, statusFilter, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNums = (() => {
    const pages: (number | '...')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); return pages; }
    pages.push(1); if (currentPage > 3) pages.push('...');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
    if (currentPage < totalPages - 2) pages.push('...'); pages.push(totalPages); return pages;
  })();
  const fmtDate = (v: string) => v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="admin-page adm-page">
      <div className="adm-topbar">
        <h1 className="adm-page-title">Review Management</h1>
        <div className="adm-topbar-right">
          <button className="adm-btn adm-btn-refresh" onClick={loadReviews} disabled={loading}>
            {loading ? <span className="adm-spin"><Loader2 size={13} /></span> : <RefreshCw size={13} />} Refresh
          </button>
        </div>
      </div>
      <div className="adm-filter-wrap">
        <div className="adm-filter-left">
          <select className="adm-filter-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
            <option value="">All Reviews</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
          </select>
          <span className="adm-count"><strong>{filtered.length}</strong> reviews</span>
        </div>
        <div className="adm-filter-right">
          <div className="adm-search-wrap">
            <span className="adm-search-icon"><Search size={13} /></span>
            <input className="adm-search-input" placeholder="Search by customer, product, comment…" value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      {isMobile && (
        <div className="mobile-card-list">
          {loading && <div className="mc-loading">Loading reviews…</div>}
          {!loading && filtered.length === 0 && <div className="mc-empty">No reviews found.</div>}
          {!loading && filtered.map(r => (
            <div className="mobile-card" key={r.id}>
              <div className="mc-top">
                <div className="mc-thumb-placeholder"><Star size={22} /></div>
                <div className="mc-info">
                  <div className="mc-title">{r.customer_name}</div>
                  <div className="mc-subtitle">{r.product_name}</div>
                </div>
                <div className="mc-badge-corner">
                  <span className={`mc-active-pill ${r.is_approved ? 'active' : 'inactive'}`}>{r.is_approved ? 'Approved' : 'Pending'}</span>
                </div>
              </div>
              <div className="mc-divider" />
              <div className="mc-meta">
                <div className="mc-meta-item"><span className="mc-meta-label">Rating:</span><Stars n={r.rating} /></div>
                <div className="mc-meta-item"><span className="mc-meta-label">Comment:</span><span className="mc-meta-value">{r.comment?.slice(0, 60)}{r.comment?.length > 60 ? '…' : ''}</span></div>
              </div>
              <div className="mc-actions">
                {!r.is_approved && <button className="mc-btn mc-btn-edit" onClick={() => handleApprove(r)}><CheckCircle2 size={12} /> Approve</button>}
                {r.is_approved && <button className="mc-btn mc-btn-edit" style={{ background: '#f59e0b' }} onClick={() => handleReject(r)}><XCircle size={12} /> Reject</button>}
                <button className="mc-btn mc-btn-delete" onClick={() => handleDeleteClick(r)}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Desktop table */}
      {!isMobile && (
        <div className="adm-table-card">
          <div className="adm-table-scroll">
            <table className="adm-table">
              <thead>
                <tr>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('customer_name')}>Customer <SA field="customer_name" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('product_name')}>Product <SA field="product_name" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('rating')}>Rating <SA field="rating" /></th>
                  <th className="adm-th">Comment</th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('is_approved')}>Status <SA field="is_approved" /></th>
                  <th className="adm-th adm-th-sort" onClick={() => handleSort('created_at')}>Date <SA field="created_at" /></th>
                  <th className="adm-th" style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="adm-loading"><span className="adm-spin" style={{ marginRight: 8 }}><Loader2 size={18} /></span>Loading reviews…</td></tr>}
                {!loading && paginated.length === 0 && <tr><td colSpan={7} className="adm-empty">No reviews found.</td></tr>}
                {!loading && paginated.map(r => (
                  <tr className="adm-row" key={r.id}>
                    <td className="adm-td"><span className="adm-name-main">{r.customer_name}</span></td>
                    <td className="adm-td"><span className="adm-text-muted" style={{ fontSize: 11 }}>{r.product_name}</span></td>
                    <td className="adm-td"><Stars n={r.rating} /></td>
                    <td className="adm-td"><span className="adm-text-muted" style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', whiteSpace: 'nowrap' }}>{r.comment}</span></td>
                    <td className="adm-td">
                      <span className={`adm-chip ${r.is_approved ? 'adm-chip-green' : 'adm-chip-amber'}`}>
                        {r.is_approved ? <><CheckCircle2 size={9} /> Approved</> : <><XCircle size={9} /> Pending</>}
                      </span>
                    </td>
                    <td className="adm-td"><span className="adm-date">{fmtDate(r.created_at)}</span></td>
                    <td className="adm-td" style={{ padding: '6px 4px' }}>
                      <div className="adm-actions-row">
                        {!r.is_approved && <button className="adm-action-btn adm-ab-approve" onClick={() => handleApprove(r)} title="Approve"><CheckCircle2 size={12} /></button>}
                        {r.is_approved && <button className="adm-action-btn adm-ab-reject" onClick={() => handleReject(r)} title="Reject"><XCircle size={12} /></button>}
                        <button className="adm-action-btn adm-ab-del" onClick={() => handleDeleteClick(r)} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="adm-pagination">
            <div className="adm-page-size-wrap"><span>Show</span><select className="adm-page-size-select" value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>{[10,20,50].map(n => <option key={n} value={n}>{n}</option>)}</select><span>per page</span></div>
            <div className="adm-page-info">{filtered.length === 0 ? '0 reviews' : `${(currentPage-1)*pageSize+1}–${Math.min(currentPage*pageSize,filtered.length)} of ${filtered.length}`}</div>
            <div className="adm-page-controls">
              <button className="adm-page-btn" disabled={currentPage===1} onClick={() => setCurrentPage(p => p-1)}>‹</button>
              <div className="adm-page-numbers">{pageNums.map((n,i) => n==='...' ? <span key={`e${i}`} style={{padding:'0 4px',color:'#94a3b8'}}>…</span> : <button key={n} className={`adm-page-num${currentPage===n?' active':''}`} onClick={() => setCurrentPage(n as number)}>{n}</button>)}</div>
              <button className="adm-page-btn" disabled={currentPage===totalPages} onClick={() => setCurrentPage(p => p+1)}>›</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedReview(null);
        }}
        onConfirm={handleDelete}
        title="Delete Review"
        message={`Are you sure you want to delete this review? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default ReviewManagement;
